import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { ADD_GROUP_MEMBER } from "../../api/group";
import { getCompanyUsers } from "../../redux/company/actions";
import { PUT_USER } from "../../api/user";
import { UseUserId } from "../../hooks/auth";
import ERRORS from "../../constants/errorStatus";

// Components
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftButton from "../../components/SoftButton";
import SoftSelect from "../../components/SoftSelect";
import SoftProfile from "../../components/ProfilePictures/SoftProfile";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
import Tooltip from "@mui/material/Tooltip";

import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ArchiveIcon from "@mui/icons-material/Archive";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";

// uuid is a library for generating unique id
import { v4 as uuidv4 } from "uuid";

// Soft UI Dashboard PRO React base styles
import colors from "../../assets/theme/base/colors";
import typography from "../../assets/theme/base/typography";
import borders from "../../assets/theme/base/borders";

import { TableBody, TableContainer, TableRow } from "@mui/material";

const PlayersTable = ({ filteredUsers, workspaces }) => {
  const { light } = colors;
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;
  const { mutateAsync: addGroupMember } = ADD_GROUP_MEMBER();
  const dispatch = useDispatch();

  const [rowsData, setRowsData] = useState([]);
  const [department, setDepartment] = useState([]);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const columns = [
    {
      name: "Name",
      align: "center",
    },
    {
      name: "Workspace",
      align: "center",
    },
    {
      name: "Status",
      align: "center",
    },
    {
      name: "Permission",
      align: "center",
    },
    {
      name: "Actions",
      align: "center",
    },
  ];

  const handleAdmin = async (id, admin) => {
    try {
      const action = admin ? "Remov" : "Add";
      const toastId = toast.loading(`${action}ing Admin ...`);
      try {
        await PUT_USER(id, { isAdmin: !admin }, false);
        dispatch(getCompanyUsers());
        toast.success(`Admin Successfully ${action}ed`, {
          id: toastId,
        });
      } catch (err) {
        if (err?.response?.status === ERRORS.UPGRADE_REQUIRED) {
          setShowUpgrade(true);
        }
        toast.error(`Admin Not ${action}ed`, {
          id: toastId,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message);
    }
  };

  workspaces = workspaces.map((item) => ({
    ...item,
    value: item._id,
    label: item.name,
  }));

  useEffect(() => {
    if (filteredUsers.length === 0) return;
    const rows = [];

    filteredUsers?.forEach((user, rowIndex) => {
      const handleChange = async (selectedOption) => {
        setDepartment(selectedOption);
        await addGroupMember({
          groupId: selectedOption.value,
          data: { userId: user.userId },
        });
        dispatch(getCompanyUsers());
      };

      const authUserId = UseUserId();
      const isLoggedInUser = () => authUserId === user?.userId;

      const rowContainer = {};
      const initialDept = workspaces.filter(
        (item) => item._id === user.groupId,
      );
      setDepartment(initialDept);
      rowContainer.Name = [
        user.image,
        `${user.firstName} ${user.lastName}`,
        user.email,
        user.userId,
      ];

      rowContainer.Worskpace = (
        <SoftSelect
          options={workspaces}
          value={department}
          onChange={handleChange}
        />
      );

      rowContainer.Status = (
        <SoftTypography variant="body2" color="text">
          {user.isVerified ? "Accepted" : "Not yet Accepted"}
        </SoftTypography>
      );

      rowContainer.Permission = (
        <ConfirmationModal
          title={`${user.isAdmin ? "Remove" : "Make"} Admin`}
          question={`Change ${user.firstName} ${user.lastName}'s admin permission?`}
          yesText="Yes, update."
          noText="No, cancel"
          onSuccess={() => handleAdmin(user.userId, user.isAdmin)}
        >
          <Tooltip
            disableFocusListener
            title={`Click to set as ${user.isAdmin ? "Player" : "Admin"}`}
            placement="top"
          >
            <SoftButton
              size="small"
              variant={user.isAdmin ? "gradient" : "outlined"}
              color="primary"
              disabled={isLoggedInUser}
              sx={{ width: "8rem" }}
            >
              {user.isAdmin ? (
                <>
                  Admin <PsychologyIcon fontSize="small" />
                </>
              ) : (
                <>
                  Player <SportsEsportsIcon fontSize="small" />
                </>
              )}
            </SoftButton>
          </Tooltip>
        </ConfirmationModal>
      );

      rows.push(rowContainer);
    });

    setRowsData(rows);
  }, []);

  const renderColumns = columns.map(({ name, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columns.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <SoftBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.md}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={0.7}
        borderBottom={`${borderWidth[1]} solid ${light.main}`}
      >
        {name.toUpperCase()}
      </SoftBox>
    );
  });

  const renderRows = rowsData.map((row, key) => {
    const rowKey = `row-${key}`;

    const tableRow = columns.map(({ name, align }) => {
      let template;

      if (Array.isArray(row[name])) {
        template = (
          <SoftBox
            key={uuidv4()}
            p={1}
            borderBottom={
              row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null
            }
            sx={{
              cursor: "pointer",
              minWidth: "300px",
            }}
          >
            <SoftBox
              display="flex"
              alignItems="center"
              py={0.5}
              px={1}
              sx={{ width: "max-content" }}
            >
              <SoftBox mr={1}>
                <SoftProfile
                  src={row[name][0]}
                  name={row[name][1]}
                  size="md"
                  borderRadius="lg"
                />
              </SoftBox>
              <SoftBox>
                <SoftTypography
                  variant="h5"
                  fontWeight="medium"
                  sx={{ width: "max-content", color: "#343A40" }}
                >
                  {row[name][1]}
                </SoftTypography>
                <SoftTypography
                  variant="caption"
                  fontWeight="regular"
                  sx={{ width: "max-content", color: "#6C757D" }}
                >
                  {row[name][2]}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        );
      } else {
        template = (
          <SoftBox
            key={uuidv4()}
            p={1}
            textAlign={align}
            borderBottom={
              row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null
            }
            color="#343A40"
            minWidth="131px"
          >
            {row[name]}
          </SoftBox>
        );
      }

      return template;
    });

    return (
      <SoftBox
        key={rowKey}
        sx={{
          alignItems: "center",
          boxShadow:
            "6px 6px 6px 0px rgba(177, 176, 176, 0.07), -4px -4px 6px 0px rgba(180, 177, 177, 0.07)",
        }}
      >
        {tableRow}
      </SoftBox>
    );
  });

  console.log(filteredUsers);

  return useMemo(
    () => (
      <TableContainer>
        <SoftBox component="thead">
          <TableRow>{renderColumns}</TableRow>
        </SoftBox>
        <TableBody>{renderRows}</TableBody>
      </TableContainer>
    ),
    [columns, rowsData],
  );
};

export default PlayersTable;
